import { useEffect, useRef, useState } from 'react';
import { FaPlus } from 'react-icons/fa6';
import Select, { components as selectComponents } from 'react-select';
import toast from 'react-hot-toast';
import Drawer from '~/components/Drawer';
import {
    inputStyles,
    labelStyles,
    requiredStyles,
} from '~/components/CampaignDetail/drawers/styles';
import { apiUrl, authHeaders } from '~/services/auth';
import {
    createMedicalProfessional,
    MedicalProfessionalDuplicateError,
    MedicalProfessionalMatch,
    searchMedicalProfessionalsByName,
} from '~/services/medical-professionals';

interface SelectOption {
    value: number | string;
    label: string;
}

interface Props {
    onCreated?: () => void;
    initialMedCenter?: { id: number; name: string };
}

const PHONE_TYPE_CHOICES: SelectOption[] = [
    { value: 'Cell', label: 'Cell' },
    { value: 'Home', label: 'Home' },
    { value: 'Office', label: 'Office' },
    { value: 'Fax', label: 'Fax' },
    { value: 'Pager', label: 'Pager' },
    { value: 'Main', label: 'Main' },
    { value: 'Direct', label: 'Direct' },
];

const US_STATES: Record<string, string> = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    DC: 'District Of Columbia',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
};

const stateOptions: SelectOption[] = Object.entries(US_STATES).map(
    ([value, label]) => ({ value, label }),
);

// react-select sets autoComplete="off" by default, but Chrome ignores "off"
// and falls back to id/label heuristics ("state" → address autofill). An
// unrecognized token disables that fallback so no address suggestions appear.
const NoAutofillInput = (props: any) => (
    <selectComponents.Input {...props} autoComplete='new-state' />
);

const searchMedicalCenters = async (state: string) => {
    const headers = authHeaders();
    const resp = await fetch(`${apiUrl}/v2/searchMedicalCenters`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ state }),
    });
    if (!resp.ok) throw new Error('Failed to load medical centers');
    return await resp.json();
};

const fetchProfessionalTypes = async () => {
    const headers = authHeaders();
    const resp = await fetch(`${apiUrl}/v2/options/medicalProfessionalTypes`, {
        headers,
    });
    if (!resp.ok) throw new Error('Failed to load professional types');
    return await resp.json();
};

const AddProfessionalModal: React.FC<Props> = ({
    onCreated,
    initialMedCenter,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loadingCenters, setLoadingCenters] = useState(false);

    const [typeOptions, setTypeOptions] = useState<SelectOption[]>([]);
    const [centerOptions, setCenterOptions] = useState<SelectOption[]>([]);

    const [selectedState, setSelectedState] = useState<SelectOption | null>(
        null,
    );
    const [selectedCenter, setSelectedCenter] = useState<SelectOption | null>(
        null,
    );
    const [addingNewCenter, setAddingNewCenter] = useState(false);
    const [newCenterName, setNewCenterName] = useState('');

    const [firstName, setFirstName] = useState('');
    const [middleName, setMiddleName] = useState('');
    const [lastName, setLastName] = useState('');
    const [profType, setProfType] = useState<number | null>(null);
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [phoneType, setPhoneType] = useState<string>('Direct');

    const [address1, setAddress1] = useState('');
    const [address2, setAddress2] = useState('');
    const [addressCity, setAddressCity] = useState('');
    const [addressState, setAddressState] = useState<SelectOption | null>(null);
    const [addressZip, setAddressZip] = useState('');
    const [npiNum, setNpiNum] = useState('');

    const [duplicateMatches, setDuplicateMatches] = useState<
        MedicalProfessionalMatch[]
    >([]);
    const [duplicateMatchType, setDuplicateMatchType] = useState<
        'email' | 'name_center' | null
    >(null);

    const [suggestions, setSuggestions] = useState<MedicalProfessionalMatch[]>(
        [],
    );
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);
    const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);
    const searchAbortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        fetchProfessionalTypes()
            .then((types: Array<{ id: number; name: string }>) => {
                setTypeOptions(
                    types.map((t) => ({ value: t.id, label: t.name })),
                );
            })
            .catch(() => {
                // non-fatal: form still works without type list
            });
    }, [isOpen]);

    useEffect(() => {
        const trimmedEmail = email.trim();
        const trimmedFirst = firstName.trim();
        const trimmedLast = lastName.trim();

        let query = '';
        if (trimmedEmail.length >= 3) {
            query = trimmedEmail;
        } else if (trimmedFirst && trimmedLast) {
            query = `${trimmedFirst} ${trimmedLast}`;
        }

        if (suggestionsDismissed || !query) {
            setSuggestions([]);
            setSuggestionsLoading(false);
            return;
        }

        const handle = setTimeout(() => {
            searchAbortRef.current?.abort();
            const controller = new AbortController();
            searchAbortRef.current = controller;
            setSuggestionsLoading(true);
            searchMedicalProfessionalsByName(query, controller.signal)
                .then((results) => {
                    if (controller.signal.aborted) return;
                    setSuggestions(results);
                })
                .catch((err) => {
                    if (err?.name === 'AbortError') return;
                    setSuggestions([]);
                })
                .finally(() => {
                    if (!controller.signal.aborted) {
                        setSuggestionsLoading(false);
                    }
                });
        }, 300);

        return () => clearTimeout(handle);
    }, [firstName, lastName, email, suggestionsDismissed]);

    const resetForm = () => {
        setSelectedState(null);
        setSelectedCenter(null);
        setCenterOptions([]);
        setAddingNewCenter(false);
        setNewCenterName('');
        setFirstName('');
        setMiddleName('');
        setLastName('');
        setProfType(null);
        setEmail('');
        setPhone('');
        setPhoneType('Direct');
        setAddress1('');
        setAddress2('');
        setAddressCity('');
        setAddressState(null);
        setAddressZip('');
        setNpiNum('');
        setDuplicateMatches([]);
        setDuplicateMatchType(null);
        setSuggestions([]);
        setSuggestionsDismissed(false);
        searchAbortRef.current?.abort();
    };

    const clearDuplicateWarning = () => {
        setDuplicateMatches([]);
        setDuplicateMatchType(null);
    };

    const handleClose = () => {
        if (isSubmitting) return;
        setIsOpen(false);
        resetForm();
    };

    const handleStateChange = async (option: SelectOption | null) => {
        setSelectedState(option);
        setSelectedCenter(null);
        setCenterOptions([]);
        setAddingNewCenter(false);
        setNewCenterName('');
        clearDuplicateWarning();

        if (!option) return;
        setLoadingCenters(true);
        try {
            const centers = await searchMedicalCenters(option.label);
            setCenterOptions(
                centers.map((c: { id: number; name: string }) => ({
                    value: c.id,
                    label: c.name,
                })),
            );
        } catch {
            toast.error('Failed to load medical centers');
        } finally {
            setLoadingCenters(false);
        }
    };

    const submitCreate = async (force: boolean) => {
        if (!firstName.trim() || !lastName.trim()) {
            toast.error('First and last name are required');
            return;
        }
        if (!initialMedCenter) {
            if (!selectedState) {
                toast.error('State is required');
                return;
            }
            if (addingNewCenter) {
                if (!newCenterName.trim()) {
                    toast.error('Medical center name is required');
                    return;
                }
            } else if (!selectedCenter) {
                toast.error('Medical center is required');
                return;
            }
        }

        setIsSubmitting(true);
        try {
            await createMedicalProfessional({
                person: {
                    first_name: firstName.trim(),
                    middle_name: middleName.trim(),
                    last_name: lastName.trim(),
                    email: email.trim(),
                    phone: phone.trim(),
                    phone_type: phoneType,
                    address_1: address1.trim(),
                    address_2: address2.trim(),
                    city: addressCity.trim(),
                    state: (addressState?.value as string) ?? '',
                    zip_code: addressZip.trim(),
                },
                med_center_id: initialMedCenter
                    ? initialMedCenter.id
                    : addingNewCenter
                    ? null
                    : (selectedCenter?.value as number),
                med_center_name: initialMedCenter
                    ? null
                    : addingNewCenter
                    ? newCenterName.trim()
                    : null,
                location: initialMedCenter
                    ? null
                    : addingNewCenter
                    ? selectedState!.label
                    : null,
                medical_professional_type_id: profType,
                npi_num: npiNum.trim() || undefined,
                force,
            });
            toast.success('Medical professional added');
            resetForm();
            setIsOpen(false);
            onCreated?.();
        } catch (err: any) {
            if (err instanceof MedicalProfessionalDuplicateError) {
                setDuplicateMatchType(err.matchType);
                setDuplicateMatches(err.matches);
            } else {
                toast.error(
                    err?.message || 'Failed to add medical professional',
                );
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        submitCreate(false);
    };

    const isValid =
        firstName.trim() &&
        lastName.trim() &&
        !isSubmitting &&
        (initialMedCenter ||
            (selectedState &&
                (addingNewCenter ? newCenterName.trim() : selectedCenter)));

    const showSuggestions =
        !suggestionsDismissed && (suggestionsLoading || suggestions.length > 0);

    return (
        <>
            <button
                onClick={() => setIsOpen(true)}
                className='inline-flex shrink-0 items-center justify-center gap-2.5 rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
            >
                <FaPlus />
                Add Professional
            </button>

            <Drawer
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                onClose={handleClose}
                title='Add Medical Professional'
            >
                <form onSubmit={handleSubmit}>
                    <label htmlFor='add-mp-first' className={labelStyles}>
                        First Name<span className={requiredStyles}>*</span>
                    </label>
                    <input
                        id='add-mp-first'
                        type='text'
                        className={`${inputStyles} mb-[16px] w-full`}
                        value={firstName}
                        onChange={(e) => {
                            setFirstName(e.target.value);
                            setSuggestionsDismissed(false);
                            clearDuplicateWarning();
                        }}
                        disabled={isSubmitting}
                        autoComplete='off'
                    />

                    <label htmlFor='add-mp-middle' className={labelStyles}>
                        Middle Name
                    </label>
                    <input
                        id='add-mp-middle'
                        type='text'
                        className={`${inputStyles} mb-[16px] w-full`}
                        value={middleName}
                        onChange={(e) => setMiddleName(e.target.value)}
                        disabled={isSubmitting}
                    />

                    <label htmlFor='add-mp-last' className={labelStyles}>
                        Last Name<span className={requiredStyles}>*</span>
                    </label>
                    <input
                        id='add-mp-last'
                        type='text'
                        className={`${inputStyles} mb-[16px] w-full`}
                        value={lastName}
                        onChange={(e) => {
                            setLastName(e.target.value);
                            setSuggestionsDismissed(false);
                            clearDuplicateWarning();
                        }}
                        disabled={isSubmitting}
                        autoComplete='off'
                    />

                    <label htmlFor='add-mp-email' className={labelStyles}>
                        Email
                    </label>
                    <input
                        id='add-mp-email'
                        type='email'
                        className={`${inputStyles} mb-[16px] w-full`}
                        value={email}
                        onChange={(e) => {
                            setEmail(e.target.value);
                            setSuggestionsDismissed(false);
                            clearDuplicateWarning();
                        }}
                        disabled={isSubmitting}
                        autoComplete='off'
                    />

                    {showSuggestions && (
                        <div className='mb-[16px] rounded-[4px] border border-stroke bg-whiten p-[12px]'>
                            <div className='mb-[8px] flex items-center justify-between'>
                                <h4 className='text-[12px] font-bold uppercase text-[#666]'>
                                    {suggestionsLoading
                                        ? 'Searching...'
                                        : `${
                                              suggestions.length
                                          } possible match${
                                              suggestions.length === 1
                                                  ? ''
                                                  : 'es'
                                          }`}
                                </h4>
                                <button
                                    type='button'
                                    onClick={() =>
                                        setSuggestionsDismissed(true)
                                    }
                                    className='text-[12px] text-[#666] hover:text-black'
                                >
                                    Dismiss
                                </button>
                            </div>
                            {!suggestionsLoading && suggestions.length > 0 && (
                                <ul className='space-y-[8px]'>
                                    {suggestions.map((m) => (
                                        <li key={m.id}>
                                            <a
                                                href={`/medical-professionals/${m.id}`}
                                                target='_blank'
                                                rel='noreferrer'
                                                className='block rounded-[4px] border border-transparent p-[8px] hover:border-stroke hover:bg-white'
                                            >
                                                <div className='text-[14px] font-bold text-primary'>
                                                    {m.name}
                                                </div>
                                                {(m.medical_center_name ||
                                                    m.location) && (
                                                    <div className='text-[12px] text-[#666]'>
                                                        {m.medical_center_name}
                                                        {m.medical_center_name &&
                                                            m.location &&
                                                            ' · '}
                                                        {m.location}
                                                    </div>
                                                )}
                                                {m.email && (
                                                    <div className='text-[12px] text-[#666]'>
                                                        {m.email}
                                                    </div>
                                                )}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    {initialMedCenter ? (
                        <div className='mb-[16px]'>
                            <span className={labelStyles}>Medical Center</span>
                            <p className='mt-[4px] text-[14px] font-medium text-body'>
                                {initialMedCenter.name}
                            </p>
                        </div>
                    ) : (
                        <>
                            <label
                                htmlFor='add-mp-state'
                                className={labelStyles}
                            >
                                State<span className={requiredStyles}>*</span>
                            </label>
                            <Select
                                menuPlacement='auto'
                                inputId='add-mp-state'
                                components={{ Input: NoAutofillInput }}
                                options={stateOptions}
                                value={selectedState}
                                onChange={handleStateChange}
                                placeholder='Select a state...'
                                className='mb-[16px]'
                                isDisabled={isSubmitting}
                            />

                            {selectedState && !addingNewCenter && (
                                <>
                                    <label
                                        htmlFor='add-mp-center'
                                        className={labelStyles}
                                    >
                                        Medical Center
                                        <span className={requiredStyles}>
                                            *
                                        </span>
                                    </label>
                                    <Select
                                        menuPlacement='auto'
                                        inputId='add-mp-center'
                                        options={centerOptions}
                                        value={selectedCenter}
                                        onChange={(opt) => {
                                            setSelectedCenter(opt);
                                            clearDuplicateWarning();
                                        }}
                                        placeholder='Select a medical center...'
                                        className='mb-[8px]'
                                        isLoading={loadingCenters}
                                        isDisabled={
                                            isSubmitting || loadingCenters
                                        }
                                    />
                                    <p className='mb-[16px] text-[14px] text-[#666]'>
                                        Center not listed?{' '}
                                        <button
                                            type='button'
                                            className='text-[#0066CC] underline hover:text-[#0052A3]'
                                            onClick={() =>
                                                setAddingNewCenter(true)
                                            }
                                        >
                                            Click here to add it.
                                        </button>
                                    </p>
                                </>
                            )}

                            {selectedState && addingNewCenter && (
                                <>
                                    <label
                                        htmlFor='add-mp-new-center'
                                        className={labelStyles}
                                    >
                                        New Medical Center Name
                                        <span className={requiredStyles}>
                                            *
                                        </span>
                                    </label>
                                    <input
                                        id='add-mp-new-center'
                                        type='text'
                                        className={`${inputStyles} mb-[8px] w-full`}
                                        value={newCenterName}
                                        onChange={(e) =>
                                            setNewCenterName(e.target.value)
                                        }
                                        disabled={isSubmitting}
                                    />
                                    <p className='mb-[16px] text-[14px] text-[#666]'>
                                        <button
                                            type='button'
                                            className='text-[#0066CC] underline hover:text-[#0052A3]'
                                            onClick={() => {
                                                setAddingNewCenter(false);
                                                setNewCenterName('');
                                            }}
                                        >
                                            Use an existing center instead
                                        </button>
                                    </p>
                                </>
                            )}
                        </>
                    )}

                    <label htmlFor='add-mp-type' className={labelStyles}>
                        Medical Professional Type
                    </label>
                    <Select
                        menuPlacement='auto'
                        inputId='add-mp-type'
                        options={typeOptions}
                        value={
                            profType
                                ? typeOptions.find(
                                      (opt) => opt.value === profType,
                                  ) || null
                                : null
                        }
                        onChange={(opt) =>
                            setProfType(opt ? (opt.value as number) : null)
                        }
                        placeholder='Select a type...'
                        className='mb-[16px]'
                        isClearable
                        isDisabled={isSubmitting}
                    />

                    <div className='mb-[24px] grid grid-cols-2 gap-[12px]'>
                        <div>
                            <label
                                htmlFor='add-mp-phone'
                                className={labelStyles}
                            >
                                Phone
                            </label>
                            <input
                                id='add-mp-phone'
                                type='tel'
                                className={`${inputStyles} w-full`}
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                disabled={isSubmitting}
                            />
                        </div>
                        <div>
                            <label
                                htmlFor='add-mp-phone-type'
                                className={labelStyles}
                            >
                                Phone Type
                            </label>
                            <Select
                                menuPlacement='auto'
                                inputId='add-mp-phone-type'
                                options={PHONE_TYPE_CHOICES}
                                value={PHONE_TYPE_CHOICES.find(
                                    (opt) => opt.value === phoneType,
                                )}
                                onChange={(opt) =>
                                    setPhoneType(
                                        opt ? (opt.value as string) : 'Direct',
                                    )
                                }
                                isDisabled={isSubmitting}
                            />
                        </div>
                    </div>

                    <label htmlFor='add-mp-address1' className={labelStyles}>
                        Address
                    </label>
                    <input
                        id='add-mp-address1'
                        type='text'
                        className={`${inputStyles} mb-[8px] w-full`}
                        value={address1}
                        onChange={(e) => setAddress1(e.target.value)}
                        placeholder='Street address'
                        disabled={isSubmitting}
                    />
                    <input
                        id='add-mp-address2'
                        type='text'
                        className={`${inputStyles} mb-[16px] w-full`}
                        value={address2}
                        onChange={(e) => setAddress2(e.target.value)}
                        placeholder='Apt, suite, unit (optional)'
                        disabled={isSubmitting}
                    />

                    <div className='mb-[24px] grid grid-cols-2 gap-[12px]'>
                        <div>
                            <label
                                htmlFor='add-mp-city'
                                className={labelStyles}
                            >
                                City
                            </label>
                            <input
                                id='add-mp-city'
                                type='text'
                                className={`${inputStyles} w-full`}
                                value={addressCity}
                                onChange={(e) => setAddressCity(e.target.value)}
                                disabled={isSubmitting}
                            />
                        </div>
                        <div>
                            <label
                                htmlFor='add-mp-addr-state'
                                className={labelStyles}
                            >
                                State
                            </label>
                            <Select
                                menuPlacement='auto'
                                inputId='add-mp-addr-state'
                                components={{ Input: NoAutofillInput }}
                                options={stateOptions}
                                value={addressState}
                                onChange={(opt) => setAddressState(opt)}
                                placeholder='State...'
                                isClearable
                                isDisabled={isSubmitting}
                            />
                        </div>
                    </div>

                    <label htmlFor='add-mp-zip' className={labelStyles}>
                        Zip Code
                    </label>
                    <input
                        id='add-mp-zip'
                        type='text'
                        className={`${inputStyles} mb-[24px] w-full`}
                        value={addressZip}
                        onChange={(e) => setAddressZip(e.target.value)}
                        disabled={isSubmitting}
                    />

                    <label htmlFor='add-mp-npi' className={labelStyles}>
                        NPI Number
                    </label>
                    <input
                        id='add-mp-npi'
                        type='text'
                        className={`${inputStyles} mb-[24px] w-full`}
                        value={npiNum}
                        onChange={(e) => setNpiNum(e.target.value)}
                        placeholder='NPI Number'
                        disabled={isSubmitting}
                    />

                    {duplicateMatchType && duplicateMatches.length > 0 && (
                        <div className='mb-[16px] border-l-4 border-[#FEBF3E] bg-[#FEF9EC] p-[16px]'>
                            <h4 className='mb-[8px] text-[14px] font-bold'>
                                {duplicateMatchType === 'email'
                                    ? 'A medical professional already exists with this email'
                                    : 'Possible duplicate at this medical center'}
                            </h4>
                            <ul className='mb-[12px] space-y-[8px]'>
                                {duplicateMatches.map((m) => (
                                    <li
                                        key={m.id}
                                        className='text-[14px]/[20px]'
                                    >
                                        <a
                                            href={`/medical-professionals/${m.id}`}
                                            target='_blank'
                                            rel='noreferrer'
                                            className='font-bold text-primary hover:underline'
                                        >
                                            {m.name}
                                        </a>
                                        {m.medical_center_name && (
                                            <span className='text-[#666]'>
                                                {' '}
                                                · {m.medical_center_name}
                                            </span>
                                        )}
                                        {m.email && (
                                            <div className='text-[12px] text-[#666]'>
                                                {m.email}
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                            {duplicateMatchType === 'email' ? (
                                <p className='text-[12px] text-[#666]'>
                                    Change the email or open the existing record
                                    to continue.
                                </p>
                            ) : (
                                <button
                                    type='button'
                                    onClick={() => submitCreate(true)}
                                    disabled={isSubmitting}
                                    className='rounded-full border-[2px] border-black px-[20px] py-[4px] text-[12px] font-bold uppercase transition-colors hover:bg-black hover:text-white disabled:opacity-50'
                                >
                                    Create anyway
                                </button>
                            )}
                        </div>
                    )}

                    <div className='mb-[30px] flex gap-[16px]'>
                        <button
                            type='button'
                            onClick={handleClose}
                            disabled={isSubmitting}
                            className='grow rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                        >
                            Cancel
                        </button>
                        <button
                            type='submit'
                            disabled={!isValid}
                            className='grow rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8] disabled:cursor-not-allowed disabled:opacity-50'
                        >
                            {isSubmitting ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </Drawer>
        </>
    );
};

export default AddProfessionalModal;
